import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { fetchApiAuth } from '../utils/api';
import {
    IncidentsResponseData, IncidentsResponseProps, IncidentFilterPayload, IncidentExportPayload,
    IncidentEmailExportPayload, IncidentCreatePayload, IncidentUpdatePayload, IncidentCompletePayload,
    IncidentReopenPayload, DiaryEntryCreatePayload, DiaryEntryUpdatePayload, IncidentEmailPreview, Incident, DiaryEntry, IncidentImage,
} from './types';
import { handleError, makeUrl } from '../utils/helpers';

export const fetchIncidents = createAsyncThunk<IncidentsResponseData, void, { rejectValue: string }>(
    'incidents/fetchIncidents',
    async (_payload, { rejectWithValue }) => {
        try {
            const response: IncidentsResponseProps = await fetchApiAuth({
                method: 'GET',
                url: 'incidents',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const fetchFilteredIncidents = createAsyncThunk<IncidentsResponseData, IncidentFilterPayload, { rejectValue: string }>(
    'incidents/fetchFilteredIncidents',
    async (payload, { rejectWithValue }) => {
        const { siteId, site, ...filterSearchUrl } = payload;
        const basePath = siteId ? `/incidents/site/${siteId}` : '/incidents';
        if (!siteId && site) {
            (filterSearchUrl as IncidentFilterPayload).site = site;
        }
        const url = makeUrl(basePath, filterSearchUrl);
        try {
            const response: IncidentsResponseProps = await fetchApiAuth({
                method: 'GET',
                url,
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

/**
 * Trigger an immediate browser download for a CSV blob returned by an export
 * endpoint (no queue, no notification — mirrors the Daily Access List export).
 *
 * @param blob     The CSV file as a Blob (server sets the text/csv content type)
 * @param filename Suggested download filename
 */
const downloadCsv = (blob: Blob, filename: string) => {
    let url: string | null = null;
    try {
        url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
    } finally {
        if (url) {
            window.URL.revokeObjectURL(url);
        }
    }
};

export const exportIncidents = createAsyncThunk<void, IncidentExportPayload, { rejectValue: string }>(
    'incidents/exportIncidents',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'incidents/export',
                body: payload,
                responseType: 'blob',
            });

            // fetchApiAuth types response.data as `any`; the download only makes
            // sense for an actual file, so guard against a non-Blob body (e.g. a
            // JSON error that slipped through with a 2xx status).
            if (!(response.data instanceof Blob)) {
                const message = 'Export failed: the server did not return a valid CSV file.';
                notification.error({ message: 'Error', description: message });
                return rejectWithValue(message);
            }

            const filename = `incidents-export-${new Date().toISOString().slice(0, 10)}.csv`;
            downloadCsv(response.data, filename);
            notification.success({
                message: 'Export Ready',
                description: 'Your incident CSV has started downloading.',
            });
            return undefined;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const createIncident = createAsyncThunk<Incident, IncidentCreatePayload, { rejectValue: string }>(
    'incidents/createIncident',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'incidents',
                body: payload,
            });
            notification.success({
                message: 'Success',
                description: 'Incident reported successfully.',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const updateIncident = createAsyncThunk<Incident, { id: number; payload: IncidentUpdatePayload }, { rejectValue: string }>(
    'incidents/updateIncident',
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'PUT',
                url: `incidents/${id}`,
                body: payload,
            });
            notification.success({
                message: 'Success',
                description: 'Incident updated successfully.',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const markCompleteIncident = createAsyncThunk<Incident, { id: number; payload: IncidentCompletePayload }, { rejectValue: string }>(
    'incidents/markCompleteIncident',
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: `incidents/${id}/complete`,
                body: payload,
            });
            notification.success({
                message: 'Success',
                description: 'Incident marked as complete.',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: errorMessage });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const reopenIncident = createAsyncThunk<Incident, { id: number; payload: IncidentReopenPayload }, { rejectValue: string }>(
    'incidents/reopenIncident',
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: `incidents/${id}/reopen`,
                body: payload,
            });
            notification.success({
                message: 'Success',
                description: 'Incident reopened.',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: errorMessage });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const createDiaryEntry = createAsyncThunk<DiaryEntry, DiaryEntryCreatePayload, { rejectValue: string }>(
    'incidents/createDiaryEntry',
    async ({ incidentId, formData }, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: `incidents/${incidentId}/diary-entries`,
                body: formData,
            });
            notification.success({
                message: 'Success',
                description: 'Diary entry added successfully.',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: errorMessage });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const previewDiaryEntryEmail = createAsyncThunk<
    IncidentEmailPreview,
    { incidentId: number; text: string; entryOccurredAt: string | null; attachmentNames: string[] },
    { rejectValue: string }
>(
    'incidents/previewDiaryEntryEmail',
    async ({ incidentId, text, entryOccurredAt, attachmentNames }, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: `incidents/${incidentId}/diary-entries/preview`,
                // snake_case to match the Laravel API.
                body: { text, entry_occurred_at: entryOccurredAt, attachment_names: attachmentNames },
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: errorMessage });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

// Renders the email an incident edit would send, without saving the edit. Takes
// the same payload as updateIncident so the preview is built from exactly what
// a save would persist.
export const previewIncidentUpdateEmail = createAsyncThunk<
    IncidentEmailPreview,
    { id: number; payload: IncidentUpdatePayload },
    { rejectValue: string }
>(
    'incidents/previewIncidentUpdateEmail',
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: `incidents/${id}/preview-update`,
                body: payload,
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: errorMessage });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const updateDiaryEntry = createAsyncThunk<DiaryEntry, DiaryEntryUpdatePayload, { rejectValue: string }>(
    'incidents/updateDiaryEntry',
    async ({ incidentId, diaryEntryId, text, entryOccurredAt }, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'PUT',
                url: `incidents/${incidentId}/diary-entries/${diaryEntryId}`,
                // Request bodies use snake_case to match the Laravel API.
                body: { text, entry_occurred_at: entryOccurredAt },
            });
            notification.success({
                message: 'Success',
                description: 'Diary entry updated successfully.',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({ message: 'Error', description: errorMessage });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const emailExportIncidents = createAsyncThunk<void, IncidentEmailExportPayload, { rejectValue: string }>(
    'incidents/emailExportIncidents',
    async (payload, { rejectWithValue }) => {
        try {
            await fetchApiAuth({
                method: 'POST',
                url: 'incidents/export-email',
                body: payload,
            });
            notification.success({
                message: 'Email Requested',
                description: 'Your request has been submitted. You will receive an email shortly.',
            });
            return undefined;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const uploadIncidentImage = createAsyncThunk<
    { incidentId: number; incidentImage: IncidentImage },
    { incidentId: number; file: File; description?: string },
    { rejectValue: string }
>(
    'incidents/uploadIncidentImage',
    async ({ incidentId, file, description }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (description) {
                formData.append('description', description);
            }
            const response: { data: { incidentImage: IncidentImage } } = await fetchApiAuth({
                method: 'POST',
                url: `incidents/${incidentId}/images`,
                body: formData,
            });
            notification.success({
                message: 'Success',
                description: 'Image uploaded successfully.',
            });
            return { incidentId, incidentImage: response.data.incidentImage };
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const updateIncidentImage = createAsyncThunk<
    { incidentId: number; incidentImage: { id: number; description: string | null } },
    { incidentId: number; imageId: number; description: string | null },
    { rejectValue: string }
>(
    'incidents/updateIncidentImage',
    async ({ incidentId, imageId, description }, { rejectWithValue }) => {
        try {
            const response: { data: { incidentImage: { id: number; description: string | null } } } = await fetchApiAuth({
                method: 'PATCH',
                url: `incidents/${incidentId}/images/${imageId}`,
                body: { description },
            });
            notification.success({
                message: 'Success',
                description: 'Image updated successfully.',
            });
            return { incidentId, incidentImage: response.data.incidentImage };
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const deleteIncidentImage = createAsyncThunk<
    { incidentId: number; imageId: number },
    { incidentId: number; imageId: number },
    { rejectValue: string }
>(
    'incidents/deleteIncidentImage',
    async ({ incidentId, imageId }, { rejectWithValue }) => {
        try {
            await fetchApiAuth({
                method: 'DELETE',
                url: `incidents/${incidentId}/images/${imageId}`,
            });
            notification.success({
                message: 'Success',
                description: 'Image deleted successfully.',
            });
            return { incidentId, imageId };
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);
